import refreshSession from 'lib/refreshSession';
import AuthData from 'lib/authData';
import AuthenticationActions from 'actions/communication/authentication';

/**
 * CD-8740 — what to do when an ordinary API call 401s for a signed-in user.
 *
 * The `access_token` cookie lives exactly as long as the JWT (2 hours). Once it
 * lapses the browser stops sending it and oso-api answers with a generic 401.
 * Left to the global handler that lands on `/unauthorized` (a "403" page) with
 * the hourly refresh loop and the `has_session` marker still alive, which reads
 * as a permissions error rather than "your session ended".
 *
 * For a **standard** (staff / consumer) user a 401 means "session over", so:
 *
 * 1. renew it once, through the existing cross-tab single-flight
 *    `refreshSession` (never a second refresh implementation);
 * 2. renewed: replay the original request, once, and carry on unnoticed;
 * 3. refused: take the ordinary SIGNOUT path and land on `/signin`.
 *
 * It stays out of the way (returns `null`) for everything else, so the global
 * handler behaves exactly as before: a 403, a 5xx, an anonymous or referral
 * visitor, and every call to `/sessions` itself. Those are the failed login
 * (bad credentials), the refresh (recursion) and the sign-out.
 */

/** The 401 flavours `callApi` produces. */
const SESSION_ENDED = ['UnauthorizedException', 'AuthTokenExpiredException'];

const SESSIONS_PATH = '/sessions';

const rethrow = { onError: (error: unknown) => Promise.reject(error) };

const nameOf = (error: unknown): string => (error as { name?: string })?.name ?? '';

/** Is a standard (staff / consumer) user's profile cached, i.e. is there a session to renew? */
export const hasStandardSession = (): boolean => {
  try {
    return Boolean((AuthData.get() as { user?: unknown } | undefined)?.user);
  } catch (_e) {
    return false;
  }
};

type Context = { requestUrl?: string } | undefined;

export type RecoveryDeps = {
  dispatch: (action: unknown) => unknown;
  /** The ordinary error handling, for failures that are not "the session is over". */
  fallback: (error: unknown) => Promise<never>;
};

/**
 * How a renewal attempt ended. One object per refresh, shared by every request
 * that waited on it.
 *
 * - `renewed`: the profile is re-cached; replay the request.
 * - `ended`: the server refused the refresh, so the session is over.
 * - neither: we could not tell (offline, 5xx); `error` is why.
 */
export type RenewOutcome = { renewed: true } | { renewed: false; ended: boolean; error?: unknown };

// A page with several requests in flight gets several 401s at once. They share
// one refresh rather than each spending the single-use refresh token.
let inFlight: Promise<RenewOutcome> | null = null;

/**
 * Renew the session through the cross-tab single-flight `refreshSession`.
 *
 * The one renew step: `callApi`'s recovery (below) and the cms axios client's
 * response interceptor (`lib/api/cms`) both go through it, so a cms 401 and an
 * oso-api 401 that land together share ONE refresh instead of racing for the
 * single-use refresh token. It only renews and reports; what to do about an
 * ended session is each caller's business (see `claimSessionEnd`).
 *
 * Never rejects.
 */
export const renewSession = (): Promise<RenewOutcome> => {
  if (!inFlight) {
    inFlight = refreshSession(rethrow)
      .then((result): RenewOutcome => {
        AuthData.store(result);
        return { renewed: true };
      })
      .catch((error): RenewOutcome => {
        if (SESSION_ENDED.includes(nameOf(error))) return { renewed: false, ended: true };
        // Offline or a 5xx: we cannot tell that the session is over, and
        // signing a user out because the connection dropped is the failure
        // mode the cookie design exists to avoid.
        return { renewed: false, ended: false, error };
      })
      .then((outcome) => {
        inFlight = null;
        return outcome;
      });
  }
  return inFlight;
};

const claimed = new WeakSet<RenewOutcome>();

/**
 * Take the right to end the session for a refused refresh. True for the first
 * caller per outcome only, so when a cms 401 and an oso-api 401 share a refresh
 * the user is signed out once, not once through each client.
 */
export const claimSessionEnd = (outcome: RenewOutcome): boolean => {
  if (claimed.has(outcome)) return false;
  claimed.add(outcome);
  return true;
};

const signOutToSignIn = (dispatch: RecoveryDeps['dispatch']) =>
  dispatch(AuthenticationActions.signOut({ redirectTo: '/signin' }));

const renewOrSignOut = ({ dispatch }: RecoveryDeps): Promise<RenewOutcome> =>
  renewSession().then((outcome) => {
    if (!outcome.renewed && outcome.ended && claimSessionEnd(outcome)) signOutToSignIn(dispatch);
    return outcome;
  });

/**
 * Try to recover from a 401 on an ordinary API call.
 *
 * @param error   what `callApi` rejected with.
 * @param context `callApi`'s error context (`requestUrl`, ...).
 * @param retry   `callApi`'s one-shot replay of the failed request, or
 *                `undefined` when this failure already is the replay.
 * @returns the promise the failed call should settle with, or `null` when this
 *          is not an expired standard session and the caller should handle the
 *          error as it always has.
 */
const recoverExpiredSession = (
  error: unknown,
  context: Context,
  retry: (() => Promise<unknown>) | undefined,
  deps: RecoveryDeps,
): Promise<unknown> | null => {
  if (!SESSION_ENDED.includes(nameOf(error))) return null;
  if (context?.requestUrl?.startsWith(SESSIONS_PATH)) return null;
  if (!hasStandardSession()) return null;

  // The renewed session was refused again straight away: it is over.
  if (!retry) {
    signOutToSignIn(deps.dispatch);
    return Promise.reject(error);
  }

  return renewOrSignOut(deps).then((outcome) => {
    if (outcome.renewed) return retry();
    return outcome.ended ? Promise.reject(error) : deps.fallback(outcome.error);
  });
};

export default recoverExpiredSession;
